import { useRef, useMemo, useCallback, useEffect } from "react";

import debounce from "lodash/debounce";
import {
  updateInput,
  getSuggestions,
  clearSuggestions,
  resetForm,
} from "../../../../store/drug-interactions/actions";

const INPUT_DEBOUNCE_MS = 250;

const useAutocomplete = ({
  dispatch,
  inputs,
  searchCondition,
  completionsURL,
}) => {
  const autosuggestRefs = useRef(new Map());

  const handleChange = ({ newValue }, index) => {
    const oldInput = inputs[index];
    const input = { ...oldInput, value: newValue };
    delete input.selection;
    updateInput(dispatch, input);
  };

  const focusNextAvailableField = ({
    forceIndex,
    excludedIndex = null,
  } = {}) => {
    const index =
      forceIndex ??
      inputs.findIndex(
        (input, inputIndex) => !input.selection && inputIndex !== excludedIndex
      );
    const ref = autosuggestRefs.current.get(index);
    if (!ref) {
      return;
    }

    ref.input.focus();
  };

  const inputsCount = inputs.length;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(focusNextAvailableField, [inputsCount]);

  const handleReset = () => {
    resetForm(dispatch);
    setTimeout(() => focusNextAvailableField({ forceIndex: 0 }), 0);
  };

  const onFetchSuggestions = useCallback(
    ({ value }) => getSuggestions(dispatch, completionsURL, value),
    [dispatch, completionsURL]
  );

  const onSuggestionsFetchRequested = useMemo(
    () => debounce(onFetchSuggestions, INPUT_DEBOUNCE_MS),
    [onFetchSuggestions]
  );

  const getSuggestionValue = (drug) => drug.completion;
  const shouldRenderSuggestions = (value, index) => {
    const hasValue = value && value.trim();
    if (!hasValue) {
      return false;
    }
    const inputRef = autosuggestRefs.current.get(index)?.input;
    const hasFocus = inputRef === document.activeElement;
    return hasFocus;
  };
  const onSuggestionsClearRequested = () => clearSuggestions(dispatch);

  const buildDisplayPlaceholder = (fieldPlaceholder, index) => {
    const basePlaceholder = fieldPlaceholder || "Drug {n}";
    return basePlaceholder.replace(/{n}/, index + 1);
  };

  const setInputRef = (input, index) =>
    autosuggestRefs.current.set(index, input);

  const handleBlur = (index) => {
    const input = inputs[index];
    if (input.selection) {
      return;
    }

    const autosuggest = autosuggestRefs.current.get(index);
    const suggestion = autosuggest.getHighlightedSuggestion();

    if (!suggestion) {
      return updateInput(dispatch, { ...input, value: "" });
    }

    updateInput(dispatch, {
      ...input,
      value: suggestion.completion,
      selection: suggestion,
    });
    focusNextAvailableField();
  };

  const handleSuggestionSelected = (
    _e,
    { suggestion, suggestionValue, method },
    index
  ) => {
    const oldInput = inputs[index];

    const autosuggest = autosuggestRefs.current.get(index);
    const { value } = autosuggest.input;

    const isSuggestionValid =
      value === searchCondition ||
      (method === "enter" && value === suggestionValue);

    if (!isSuggestionValid) {
      const input = { ...oldInput, value };
      delete input.selection;
      updateInput(dispatch, input);
      return;
    }

    const input = {
      ...oldInput,
      value: suggestionValue,
      selection: suggestion,
    };

    updateInput(dispatch, input);

    // react-autosuggest brings the focus back to the field
    // after selecting a suggestion by clicking on it.
    // So we need to delay bringing the focus over to the next field
    // until next frame.
    setTimeout(() => focusNextAvailableField({ excludedIndex: index }), 0);
  };

  return {
    setInputRef,
    handleChange,
    handleBlur,
    getSuggestionValue,
    shouldRenderSuggestions,
    handleSuggestionSelected,
    onSuggestionsFetchRequested,
    onSuggestionsClearRequested,
    buildDisplayPlaceholder,
    handleReset,
  };
};

export default useAutocomplete;
